import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  ChatConversation,
  ChatService,
  ChatStatus,
  ChatUserType,
  PaginatedChatConversations,
} from '../../../../services/chat';

@Component({
  selector: 'app-allchats',
  templateUrl: './allchats.component.html',
  styleUrl: './allchats.component.scss',
})
export class AllchatsComponent implements OnInit {
  conversations: ChatConversation[] = [];

  isLoading = false;
  isUpdating = false;
  errorMessage = '';

  searchKeyword = '';
  selectedStatus = '';
  selectedUserType = '';

  page = 1;
  lastPage = 1;
  perPage = 10;
  total = 0;

  openCount = 0;
  pendingCount = 0;
  closedCount = 0;

  statusOptions: { value: ChatStatus | ''; label: string }[] = [
    { value: '', label: 'كل الحالات' },
    { value: 'open', label: 'مفتوح' },
    { value: 'pending', label: 'قيد المتابعة' },
    { value: 'closed', label: 'مغلق' },
  ];

  userTypeOptions: { value: ChatUserType | ''; label: string }[] = [
    { value: '', label: 'كل المستخدمين' },
    { value: 'guest', label: 'زائر' },
    { value: 'candidate', label: 'مرشح' },
    { value: 'company', label: 'شركة' },
    { value: 'admin', label: 'أدمن' },
  ];

  constructor(
    private chatService: ChatService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadConversations(1);
  }

  loadConversations(page: number = 1): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.page = page;

    this.chatService
      .getAdminConversations({
        status: this.selectedStatus as ChatStatus | '',
        user_type: this.selectedUserType as ChatUserType | '',
        search: this.searchKeyword.trim(),
        per_page: this.perPage,
        page: this.page,
      })
      .subscribe({
        next: (res: PaginatedChatConversations) => {
          this.conversations = res.data || [];
          this.page = res.current_page || 1;
          this.lastPage = res.last_page || 1;
          this.perPage = res.per_page || 10;
          this.total = res.total || 0;

          this.calculatePageStats();

          this.isLoading = false;
        },
        error: () => {
          this.errorMessage = 'حصل خطأ أثناء تحميل المحادثات';
          this.isLoading = false;
        },
      });
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchKeyword = input.value;
  }

  onStatusFilterChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedStatus = select.value;
    this.loadConversations(1);
  }

  onUserTypeFilterChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedUserType = select.value;
    this.loadConversations(1);
  }

  resetFilters(): void {
    this.searchKeyword = '';
    this.selectedStatus = '';
    this.selectedUserType = '';
    this.loadConversations(1);
  }

  openConversation(conversation: ChatConversation): void {
    this.router.navigate(['/conversation', conversation.id]);
  }

  updateStatus(conversation: ChatConversation, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const oldStatus = conversation.status;
    const status = select.value as ChatStatus;

    if (!status || status === oldStatus || this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    this.errorMessage = '';

    this.chatService.adminUpdateStatus(conversation.id, status).subscribe({
      next: (res) => {
        conversation.status = res.conversation.status;
        conversation.closed_at = res.conversation.closed_at;
        this.calculatePageStats();
        this.isUpdating = false;
      },
      error: () => {
        select.value = oldStatus;
        this.errorMessage = 'حصل خطأ أثناء تحديث حالة المحادثة';
        this.isUpdating = false;
      },
    });
  }

  getLatestMessage(conversation: ChatConversation): string {
    const latest = conversation.latest_message;

    if (!latest) {
      return 'لا توجد رسائل';
    }

    if (latest.message) {
      return latest.message;
    }

    if (latest.message_key) {
      return this.getMessageKeyLabel(latest.message_key);
    }

    return 'رسالة نظام';
  }

  getMessageKeyLabel(key: string): string {
    const normalizedKey = key.toLowerCase();

    switch (normalizedKey) {
      case 'atw.chat.messages.reply':
        return 'تم استلام الرسالة، وسيتم الرد في أقرب وقت.';
      case 'atw.chat.messages.welcome':
        return 'أهلا بك في الشحات إن';
      case 'atw.chat.messages.help':
        return 'اختر سؤالا سريعا أو اكتب رسالتك وسنساعدك.';
      case 'atw.chat.quickmessages.jobs':
        return 'أريد معرفة الوظائف المتاحة';
      case 'atw.chat.quickmessages.cv':
        return 'أحتاج مساعدة في السيرة الذاتية';
      case 'atw.chat.quickmessages.travel':
        return 'أريد معرفة خطوات السفر';
      default:
        return key;
    }
  }

  getLatestSenderLabel(conversation: ChatConversation): string {
    const sender = conversation.latest_message?.sender_type;

    switch (sender) {
      case 'user':
        return 'العميل';
      case 'admin':
        return 'الأدمن';
      case 'bot':
        return 'المساعد';
      case 'system':
        return 'النظام';
      default:
        return '-';
    }
  }

  getLatestSenderIcon(conversation: ChatConversation): string {
    const sender = conversation.latest_message?.sender_type;

    switch (sender) {
      case 'user':
        return 'عميل';
      case 'admin':
        return 'رد';
      case 'bot':
        return 'مساعد';
      case 'system':
        return 'نظام';
      default:
        return '-';
    }
  }

  getUserDisplayName(conversation: ChatConversation): string {
    if (conversation.name) {
      return conversation.name;
    }

    if (conversation.user?.first_name || conversation.user?.last_name) {
      return `${conversation.user?.first_name || ''} ${conversation.user?.last_name || ''}`.trim();
    }

    if (conversation.phone) {
      return conversation.phone;
    }

    if (conversation.email) {
      return conversation.email;
    }

    return 'زائر';
  }

  getUserSubText(conversation: ChatConversation): string {
    if (conversation.phone) {
      return conversation.phone;
    }

    if (conversation.email) {
      return conversation.email;
    }

    if (conversation.session_id) {
      return conversation.session_id;
    }

    return 'جلسة زائر';
  }

  getAvatarLetter(conversation: ChatConversation): string {
    const name = this.getUserDisplayName(conversation);

    if (!name || name === 'زائر') {
      return 'ز';
    }

    return name.charAt(0).toUpperCase();
  }

  getUserTypeLabel(type: string): string {
    switch (type) {
      case 'candidate':
        return 'مرشح';
      case 'company':
        return 'شركة';
      case 'admin':
        return 'أدمن';
      default:
        return 'زائر';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'open':
        return 'مفتوح';
      case 'pending':
        return 'قيد المتابعة';
      case 'closed':
        return 'مغلق';
      default:
        return status || '-';
    }
  }

  formatDate(date: string | null): string {
    if (!date) {
      return '-';
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return '-';
    }

    return parsedDate.toLocaleString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getPages(): number[] {
    const pages: number[] = [];
    const maxPages = 5;

    let start = Math.max(1, this.page - 2);
    let end = Math.min(this.lastPage, start + maxPages - 1);

    if (end - start < maxPages - 1) {
      start = Math.max(1, end - maxPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  private calculatePageStats(): void {
    this.openCount = this.conversations.filter(
      (item) => item.status === 'open'
    ).length;

    this.pendingCount = this.conversations.filter(
      (item) => item.status === 'pending'
    ).length;

    this.closedCount = this.conversations.filter(
      (item) => item.status === 'closed'
    ).length;
  }
}
